import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppStateService } from 'core/app-state/app-state.service';
import { AppState } from 'core/common-entities/app-state.class';
import { NotificationType } from 'core/notification/notification-config.class';
import { AuthenticationService } from '../authentication/authentication.service';
import { LoggerService } from '../logger/logger.service';
import { NotificationService } from '../notification/notification.service';
import { ResponseType } from './response-type.enum';

@Injectable()
export class ApiService {

  private queuedRequests: any = {};
  private runningQueuedRequests: any = {};
  private stringSource = 'assets/appStrings.json';
  private state: AppState;
  apiRoot = '';

  constructor(
    private http: HttpClient,
    private loggerService: LoggerService,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
    private appStateService: AppStateService
  ) {
    this.state = this.appStateService.state;
    this.initializeApiRoot(this.appStateService.state.isOnline);

    // on offline state toggle, check if updates need to be received, set api root
    this.appStateService.offlineStateToggle$.subscribe(isOnline => {
      this.state = this.appStateService.state;

      // if is online, set API root for online
      // if it's not online, and no receive updates are pending, set API root for offline
      // if it's not online and receive updates are pending, don't do anything, handled at below subscribe
      if (isOnline || !this.appStateService.receiveOnlineUpdatesInProgress) {
        this.initializeApiRoot(isOnline);
      }
    });

    // when receive online updates is done, set api root
    this.appStateService.receiveOnlineUpdatesDone$.subscribe(() => this.initApiRootAndState(this.state.isOnline));
  }

  /**
   * Initializes API root
   */
  private initializeApiRoot(isOnline: boolean) {
    if (isOnline) {
      this.apiRoot = environment.webRoot;
    } else {
      this.apiRoot = environment.offlineApiRoot;
    }
  }

  // --------------- Queue management methods ---------------

  /**
   * queues the request execution.
   * Used to bypass multiple requests if triggered multiple times before they're finished executing
   * @param onCompleteAllCallback: callback used after all requests fulfils
   */
  queueRequest<T>(url: string, payload: any, callback: (...args: any[]) => any,
    requestType: string, onCompleteAllCallback?, errorCallback?) {
    let action;

    if (requestType === 'post') {
      action = this.post<T>(url, payload);
    } else {
      action = this.get<T>(url);
    }

    if (!this.queuedRequests[url]) {
      this.queuedRequests[url] = [];
    }

    // since requests are Observables,having the request split like this
    // allows us to use the subscriber "complete" parameter to continue the processing loop
    this.queuedRequests[url].push({
      action,
      errorCallback,
      callback,
      onCompleteAllCallback,
    });
    this.processQueue(url);
  }

  private processQueue(url: string) {
    if (!this.runningQueuedRequests[url] && this.queuedRequests[url] && this.queuedRequests[url].length) {
      const currentRequest = this.queuedRequests[url].shift();
      this.runningQueuedRequests[url] = true;
      currentRequest.action.subscribe(
        result => currentRequest.callback(result),
        err => {
          this.processingComplete(url);
          if (currentRequest.errorCallback) {
            currentRequest.errorCallback(err);
          }
        },
        () => {
          this.processingComplete(url);
          if (currentRequest.onCompleteAllCallback &&
            this.queuedRequests[url].length === 0 && this.runningQueuedRequests[url] === false) {
            currentRequest.onCompleteAllCallback();
          }
        });
    }
  }

  /**
   * Called when processing is complete
   * @param url - The queue key
   */
  private processingComplete(url: string) {
    this.runningQueuedRequests[url] = false;
    this.processQueue(url);
  }

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  /**
   * POST method
   * @param url The url
   * @param payload The object sent to the backend. < This should be one of the existing data types: User|Account|etc. >
   */
  post<T>(url: string, payload: any, options = {}) {
    return this.http.post<T>(this.apiRoot + url, payload, { headers: this.getHeaders(), ...options }).pipe(
      catchError(this.handleError<any>(url))
    );
  }

  /**
   * POST method with full URL
   * @param fullUrl - The Full URL including protocol, domain...
   * @param payload The object sent to the backend. < This should be one of the existing data types: User|Account|etc. >
   */
  postGeneric<T>(fullUrl: string, payload: any, withHeaders = false) {
    return this.http.post<T>(fullUrl, payload, { headers: withHeaders ? this.getHeaders() : this.getResponseHeaders(false) }).pipe(
      catchError(this.handleError<any>(fullUrl))
    );
  }

  /**
   * POST method that returns a BLOB object
   * @param url The url
   * @param payload The object sent to the backend. < This should be one of the existing data types: User|Account|etc. >
   */
  postBlob(url: string, payload: any) {
    return this.http.post(this.apiRoot + url, payload, { headers: this.getHeaders(true), responseType: ResponseType.ArrayBuffer }).pipe(
      catchError(this.handleError<any>(url))
    );
  }

  /**
   * GET method
   * @param url - The URL
   */
  get<T>(url: string, options = {}) {
    return this.http.get<T>(this.apiRoot + url, { headers: this.getHeaders(), ...options }).pipe(
      catchError(this.handleError<any>(url))
    );
  }

  /**
   * GET method with full URL
   * @param fullUrl - The Full URL including protocol, domain...
   */
  getGeneric<T>(fullUrl: string, withHeaders = false) {
    return this.http.get<T>(fullUrl, { headers: withHeaders ? this.getHeaders() : this.getResponseHeaders(false) }).pipe(
      catchError(this.handleError<any>(fullUrl))
    );
  }

  /**
   * GET blob method
   * @param url - The URL
   */
  getBlob(url: string) {
    return this.http.get(this.apiRoot + url, { headers: this.getHeaders(true), responseType: ResponseType.ArrayBuffer }).pipe(
      catchError(this.handleError<any>(url))
    );
  }

  /**
   * DELETE method with full URL
   * @param fullUrl - The Full URL including protocol, domain...
   */
  deleteGeneric<T>(fullUrl: string, withHeaders = false) {
    return this.http.delete<T>(fullUrl, { headers: withHeaders ? this.getHeaders() : this.getResponseHeaders(false) }).pipe(
      catchError(this.handleError<any>(fullUrl))
    );
  }

  /**
   * PUT method with full URL
   * @param fullUrl - The Full URL including protocol, domain...
   * @param payload The object sent to the backend. < This should be one of the existing data types: User|Account|etc. >
   */
  putGeneric<T>(fullUrl: string, payload: any, withHeaders = false) {
    return this.http.put<T>(fullUrl, payload, { headers: withHeaders ? this.getHeaders() : this.getResponseHeaders(false) }).pipe(
      catchError(this.handleError<any>(fullUrl))
    );
  }

  /**
   * Gets the App Strings JSON file
   */
  getAppStrings() {
    return this.http.get(this.stringSource).pipe(
      catchError(this.handleError<any>(this.stringSource))
    );
  }

  /**
   * Handles errors
   * @param url - The URL
   */
  private handleError<T>(url: string) {
    return (error: any): Observable<T> => {
      // decode error if response is ArrayBuffer
      this.decodeArrayBufferError(error);

      // building messages
      let message = error.error;
      if (typeof message !== 'string' && message) {
        message = error.error.title || error.error.text;
      }

      if (!message) {
        message = error.message;
      }

      if (message.indexOf('<html') !== -1) {
        message = 'Unexpected server error. Please try again later or contact support.';
      }

      // check if session expired
      if (error.status === 401) {
        this.loggerService.logError('UI_SESSION: User was logged out due to unauthorized response from back-end.');
        this.authenticationService.logout();
        return throwError(error);
      }

      if (error.status === 0) {
        if (this.state.isElectron && !this.state.isOnline) {
          this.appStateService.setLocalServerFailure();
        }
        // if no internet
        message = 'Unknown error. Please check your internet connection.';
      }

      const logMessage = this.getErrorMessage(error);

      // log and show error
      this.loggerService.logError(`FE: Call to ${url} failed: ${logMessage}`);

      // Don't show notification, will be handled in the login component
      if (error.url && error.url.includes('/api/identity/offlineUser')) {
        return throwError(error);
      }

      if (!this.state.isInternetConnected && error.status === 403 && error.url.includes('/api/identity/userinfo')) {
        this.loggerService.logError(`FE: User does not have access to use the app in offline mode. Err: ${logMessage}`);
      } else {
        this.notificationService.showNotification(`Error: ${message}`, NotificationType.Error, true);
      }

      return throwError(error);
    };
  }

  getErrorMessage(error) {
    return error.error ? `Error: ${error.message}. Details: ${JSON.stringify(error.error)}` : `Error: ${error.message}`;
  }

  /**
   * Decoding array buffer error
   * @param error error
   */
  decodeArrayBufferError(error) {
    if (error.error && typeof error.error !== 'string' && error.error.byteLength) {
      error.error = String.fromCharCode.apply(null, new Uint8Array(error.error));
    }
  }

  /**
   * Log custom messages
   * @param as logMessage
   */
  logCustomErrors(logMessage: string) {
    this.loggerService.logError(logMessage);
  }

  /**
   * Gets request headers
   * @param as isBlob - Specified if request is for blob
   * @param as skipToken
   */
  getHeaders(isBlob = false) {
    let header = this.getResponseHeaders(isBlob);
    const token = this.authenticationService.getToken();
    if (token) {
      header = header.set('Authorization', token);
    }

    return header;
  }

  private getResponseHeaders(isBlob) {
    return new HttpHeaders({
      'Response-Type': isBlob ? ResponseType.ArrayBuffer : ResponseType.JSON
    });
  }

  /**
   * Initializes API root and sets the local state according to appStateService
   * @param as isOnline
   */
  private initApiRootAndState(isOnline: boolean) {
    this.initializeApiRoot(isOnline);
    this.state = this.appStateService.state;
  }
}
